import { useEffect } from "react";
import { updatePageSEO } from "@/lib/seo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, ExternalLink } from "lucide-react";

// TODO: Add real events here as they are confirmed
const events = [
  {
    name: '[Event Name — TBD]',
    date: '[Date TBD]',
    location: '[Venue / City — TBD]',
    description: '[Event description coming soon]',
    link: '#',
  },
];

// TODO: Add news items here as coverage and announcements come in
const news = [
  {
    headline: '[News headline — TBD]',
    date: '[Date]',
    body: '[News item body — TBD]',
    link: '#',
  },
];

const NewsEvents = () => {
  useEffect(() => {
    updatePageSEO({
      title: "News & Events | Diane Corso",
      description: "Stay updated with the latest news, events, and appearances from horror author Diane Corso.",
      keywords: "Diane Corso events, book signings, author appearances, horror author news",
      ogImage: "https://www.dianecorsofiction.com/images/diane-corso-headshot.webp",
      canonical: "https://www.dianecorsofiction.com/news-events"
    });
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gothic-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-cinzel font-bold text-blood-red mb-6">News & Events</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Upcoming appearances, book signings, and the latest news from Diane Corso
          </p>
          <div className="w-24 h-1 bg-blood-red mx-auto mt-6"></div>
        </div>

        {/* Events Section */}
        <div className="mb-20">
          <h2 className="text-3xl font-cinzel font-bold text-gothic-white mb-10">Upcoming Events</h2>
          {events.length === 0 ? (
            <Card className="bg-gothic-charcoal border-gothic-gray">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400">No events scheduled at this time. Check back soon!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {events.map((event, idx) => (
                <Card key={idx} className="bg-gothic-charcoal border-gothic-gray hover:transform hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-playfair font-bold text-gothic-white mb-4">{event.name}</h3>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-gray-400">
                        <Calendar className="w-4 h-4 mr-3 text-blood-red" />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex items-center text-gray-400">
                        <MapPin className="w-4 h-4 mr-3 text-blood-red" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    <p className="text-gray-300 mb-6">{event.description}</p>
                    <Button asChild className="w-full bg-blood-red hover:bg-red-700" disabled={event.link === '#'}>
                      <a href={event.link} target="_blank" rel="noopener noreferrer">
                        Learn More
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* News Section */}
        <div>
          <h2 className="text-3xl font-cinzel font-bold text-gothic-white mb-10">Latest News</h2>
          {news.length === 0 ? (
            <Card className="bg-gothic-charcoal border-gothic-gray">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400">No news items at this time. Check back soon!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {news.map((item, idx) => (
                <Card key={idx} className="bg-gothic-charcoal border-gothic-gray hover:border-blood-red transition-colors duration-300">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-playfair font-bold text-gothic-white flex-1">{item.headline}</h3>
                      <span className="text-sm text-gray-400 ml-4">{item.date}</span>
                    </div>
                    <p className="text-gray-300 mb-4">{item.body}</p>
                    {item.link !== '#' && (
                      <Button asChild variant="link" className="text-blood-red hover:text-gothic-white p-0 h-auto">
                        <a href={item.link} target="_blank" rel="noopener noreferrer">
                          Read More <ExternalLink className="ml-2 w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsEvents;
